import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, Check } from "lucide-react";

const OPTIONS = [
  { key: "awaiting",   label: "AWAITING",   color: "#9ca3af" },
  { key: "replied",    label: "REPLIED",    color: "#34C759" },
  { key: "interested", label: "INTERESTED", color: "#FFC107" },
  { key: "declined",   label: "DECLINED",   color: "#FF3B30" },
  { key: "ghosted",    label: "GHOSTED",    color: "#71717a" },
];

/**
 * Compact dropdown that updates a sponsor's response_status via PATCH.
 * Two variants: 'pill' (small, kanban-card-friendly) and 'full' (detail-modal-friendly).
 */
export default function ResponseStatusDropdown({ sponsor, variant = "pill", onChange }) {
  const currentKey = (sponsor?.response_status || "awaiting").toLowerCase();
  const current = OPTIONS.find((o) => o.key === currentKey) || OPTIONS[0];
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const setStatus = async (key, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (key === currentKey) { setOpen(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/sponsors/${sponsor.sponsor_id}/response`, { response_status: key });
      onChange?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update response");
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(!open); }}
        disabled={saving}
        className={variant === "full"
          ? "sd-btn sd-btn-ghost h-10 px-3 text-xs w-full justify-between"
          : "font-mono text-[10px] uppercase px-1.5 py-0.5 border flex items-center gap-1 hover:brightness-125 transition-all"}
        style={{ color: current.color, borderColor: current.color + "66" }}
        data-testid={`response-status-btn-${sponsor.sponsor_id}`}
        title="Update response status"
      >
        <span className="w-1 h-1 rounded-full shrink-0" style={{ background: current.color }} />
        {current.label}
        <ChevronDown size={variant === "full" ? 12 : 9} strokeWidth={1.5} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div
            className="absolute z-40 mt-1 right-0 bg-[#0e0e0f] border border-[#27272a] min-w-[160px] shadow-2xl"
            data-testid={`response-status-menu-${sponsor.sponsor_id}`}
          >
            {OPTIONS.map((o) => (
              <button
                key={o.key}
                onClick={(e) => setStatus(o.key, e)}
                className="w-full flex items-center gap-2 text-left font-mono text-[11px] uppercase px-3 py-2 hover:bg-[#141414] transition-colors"
                style={{ color: o.color }}
                data-testid={`response-status-opt-${o.key}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: o.color }} />
                <span className="flex-1">{o.label}</span>
                {o.key === currentKey && <Check size={12} strokeWidth={1.5} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
